import { Button, HookedForm, TextField, PhoneField } from '@app/components';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLeaveReview } from './LeaveReview.hooks';
import { Star } from '@app/components/Star/Star';
import AttachIcon from '@app/assets/icons/attach.svg?react';
import { AIDropdown } from '@app/components/AIDropdown/AIDropdown';
import { BAD_REVIEW_REASONS } from '@app/utils/consts';
import clsx from 'clsx';
import { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { BaseDialog } from '@app/components/BaseDialog/BaseDialog';
import { PreviewReviewBox } from '@app/components/ReviewBox/PreviewReviewBox';
import HelpDeskIcon from '@app/assets/icons/help-desk.svg?react';

import { FieldValues } from 'react-hook-form';
import YelpIcon from '@app/assets/icons/integrations/yelp.svg?react';
import GoogleIcon from '@app/assets/icons/Google.svg?react';
import GlobeIcon from '@app/assets/icons/globe-05.svg?react';
import InfoIcon from '@app/assets/icons/info.svg?react';
import { updateRequest } from '@app/services/ratings';
import { getCampaignPageDesigns } from '@app/services/campaigns';
import { CampaignPageDesignInterface } from '@app/models/campaigns';


export const LeaveReview = () => {
  
  const copyToClipboard = async (text: string) => {
      await navigator.clipboard.writeText(text);
  
  };

  const navigate = useNavigate();
  const params = useParams();
  const requestId = params.requestId ? params.requestId.split('&')[0] : '';
  
  // Extract URL parameters
  const [isBasicPlan, setIsBasicPlan] = useState(false);
  
  // State for dynamic design
  const [pageDesign, setPageDesign] = useState<CampaignPageDesignInterface | null>(null);
  const [feedbackDesign, setFeedbackDesign] = useState<CampaignPageDesignInterface | null>(null);
  const [isLoadingDesign, setIsLoadingDesign] = useState(false);
  
  useEffect(() => {
    // Extract parameters from both search params and pathname
    let planType = null;
    
    // First, try standard query parameters
    const urlParams = new URLSearchParams(window.location.search);
    planType = urlParams.get('p');
    
    // If not found, check if parameters are in the pathname (after requestId)
    if (!planType) {
      const pathParts = window.location.pathname.split('/');
      const requestIdPart = pathParts[pathParts.length - 1]; // Get the last part
      
      // Check if there are parameters appended to the requestId
      if (requestIdPart && requestIdPart.includes('&')) {
        const paramsString = requestIdPart.split('&').slice(1).join('&'); // Everything after first &
        const pathParams = new URLSearchParams(paramsString);
        planType = pathParams.get('p');
      }
    }
    
    const basicPlan = planType === 'BASIC';
    setIsBasicPlan(basicPlan);
  
  }, []);
  
  const {
    leaveReviewForm,
    handleStarClick,
    ratingValue,
    actionsFull,
    handleChange,
    thumbnails,
    setThumbnails,
    removeFile,
    handleSubmit,
    handleBadReason,
    requestData,
    handleDeclineAiSuggestion,
    isSubmitting,
    accessToken,
    setAccessToken,
    yelpUrl,
    googleUrl,
  } = useLeaveReview();

  // Fetch campaign page design
  useEffect(() => {
    const fetchPageDesign = async () => {
      // Both campaignId and businessId are returned from the requests endpoint
      const campaignId = requestData?.data?.campaignId;
      const businessId = requestData?.data?.businessId;
      // Wait for access token, campaign ID, and business ID to be available
      if (!campaignId || !accessToken || !businessId) {
        return;
      }
      
      setIsLoadingDesign(true);
      try {
        const response = await getCampaignPageDesigns(
          campaignId,
          accessToken,
          businessId
        );
        
        if (response.data.total > 0) {
          // Separate review and feedback designs
          const reviewDesign = response.data.pageDesigns.find(design => design.type === 'reviewFormDesign');
          const feedbackDesignData = response.data.pageDesigns.find(design => design.type === 'feedbackFormDesign');
          
          if (reviewDesign) {
            setPageDesign(reviewDesign);
          }
          
          if (feedbackDesignData) {
            setFeedbackDesign(feedbackDesignData);
          }
        }
      } catch (error) {
        console.error('Failed to fetch page design:', error);
      } finally {
        setIsLoadingDesign(false);
      }
    };
    
    fetchPageDesign();
  }, [requestData?.data?.campaignId, accessToken, requestData?.data?.businessId]);

  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const [hasStoppedTyping, setHasStoppedTyping] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    // Extract token from both search params and pathname
    let token = null;
    
    // First, try standard query parameters
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get('t');
    
    // If not found, check if parameters are in the pathname (after requestId)
    if (!token) {
      const pathParts = window.location.pathname.split('/');
      const requestIdPart = pathParts[pathParts.length - 1]; // Get the last part
      
      // Check if there are parameters appended to the requestId
      if (requestIdPart && requestIdPart.includes('&')) {
        const paramsString = requestIdPart.split('&').slice(1).join('&'); // Everything after first &
        const pathParams = new URLSearchParams(paramsString);
        token = pathParams.get('t');
      }
    }

    if (token) {
      setAccessToken(token);
    } 
  }, [setAccessToken]);

  // Track origin parameter and update request accordingly
  useEffect(() => {
    const trackRequestOpen = async () => {
      if (!requestId || !accessToken || !requestData?.data?.businessId) {
        return;
      }

      // Extract origin parameter from URL
      const urlParams = new URLSearchParams(window.location.search);
      const origin = urlParams.get('origin');
      
      // Also check pathname for origin parameter (similar to planType logic)
      let pathOrigin = null;
      const pathParts = window.location.pathname.split('/');
      const requestIdPart = pathParts[pathParts.length - 1];
      if (requestIdPart && requestIdPart.includes('&')) {
        const paramsString = requestIdPart.split('&').slice(1).join('&');
        const pathParams = new URLSearchParams(paramsString);
        pathOrigin = pathParams.get('origin');
      }

      const finalOrigin = origin || pathOrigin;
      
      if (finalOrigin) {
        try {
          const updateData: { openedEmail?: boolean; openedSms?: boolean } = {};
          
          if (finalOrigin === 'email') {
            updateData.openedEmail = true;
          } else if (finalOrigin === 'sms') {
            updateData.openedSms = true;
          }

          if (Object.keys(updateData).length > 0) {
            await updateRequest(
              requestId,
              updateData,
              accessToken,
              requestData.data.businessId
            );
          }
        } catch (error) {
          console.error('Failed to track request open:', error);
        }
      }
    };

    trackRequestOpen();
  }, [requestId, accessToken, requestData?.data?.business?.id]);

  const handleTypingPause = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    setHasStoppedTyping(false);

    typingTimeoutRef.current = setTimeout(() => {
      setHasStoppedTyping(true);
    }, 2000);
  }, []);

  const handleSubmitClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const reviewText = leaveReviewForm.watch('review') || '';
    const rating = leaveReviewForm.watch('rating') || 0;
    const visibility = leaveReviewForm.watch('visibility') || 'private';
    
    // For bad reviews (1-2 stars) with private visibility, submit directly as feedback
    if (rating >= 1 && rating <= 2 && visibility === 'private') {
      // Submit as feedback (isFeedback: true)
      handleSubmit(leaveReviewForm.getValues(), true);
      return;
    }
    
    // For bad reviews (1-2 stars) with public visibility, this should not happen as platform buttons should show
    if (rating >= 1 && rating <= 2 && visibility === 'public') {
      setShowConfirmation(true);
      return;
    }
    
    // For good reviews (3+ stars), check if we should go directly to platform
    if (rating >= 3) {
      // If in reviewFormDesign mode with defaultPlatform, handle platform submission directly
      if (currentDesign?.type === 'reviewFormDesign' && currentDesign?.defaultPlatform) {
        const platform = currentDesign.defaultPlatform.toLowerCase();
        const platformUrl = platform === 'google' ? googleUrl : platform === 'yelp' ? yelpUrl : null;
        
        if (platformUrl) {
          // Copy review text to clipboard
          copyToClipboard(reviewText);
          
          // Store that this platform was used
    const reviewData = {
      review: reviewText,
      rating: rating,
      yelpUrl: yelpUrl,
      googleUrl: googleUrl,
      businessName: requestData?.data?.business?.name,
      businessAddress: requestData?.data?.business?.address,
      thumbnails: thumbnails,
      timestamp: new Date().toISOString(),
            usedPlatform: platform
    };
    localStorage.setItem('reviewData', JSON.stringify(reviewData));
    
          // Submit review to database with isFeedback: false for platform reviews
          handleSubmit(leaveReviewForm.getValues(), false);
          
          // Open platform review page
          window.open(platformUrl, '_blank', 'noopener,noreferrer');
          
          // Redirect to thank you page for positive reviews with access token
          const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
          navigate(thankYouUrl);
          return;
        }
      }
      
      // Fallback to showing confirmation (which will show platform buttons)
      setShowConfirmation(true);
      return;
    }
    
    // For no rating, show confirmation
    setShowConfirmation(true);
  };

  // Create a wrapper function that matches SubmitHandler type
  const handleFormSubmit = (values: FieldValues) => {
    const rating = values.rating || 0;
    const visibility = values.visibility || 'private';
    
    // For bad reviews (1-2 stars) with private visibility, submit as feedback
    if (rating >= 1 && rating <= 2 && visibility === 'private') {
      handleSubmit(values, true);
    } else {
      // For other cases, submit as regular review
      handleSubmit(values, false);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmation(false);
  };


  // Helper to count words
  const reviewText = leaveReviewForm.watch('review') || '';

  // Construct a default BusinessReviews object for preview
  const previewReview = useMemo(() => {
    const photos = thumbnails.map((url, i) => ({
      id: `preview-photo-${i}`,
      businessReviewId: 'preview',
      url,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));

    return {
      id: 'preview',
      firstName: 'You',
      lastName: '',
      review: reviewText,
      rating: leaveReviewForm.watch('rating') || 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      business: requestData?.data?.business || {},
      customer: {
        firstName: 'You',
        lastName: '',
        email: 'your@email.com',
        userId: 'preview-user',
        id: 'preview-user',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lists: [],
        tags: [],
      },
      photos,
    };
  }, [
    reviewText,
    leaveReviewForm.watch('rating'),
    requestData?.data?.business,
    thumbnails,
  ]);



  // Function to get the appropriate design based on rating
  const getCurrentDesign = () => {
    const currentRating = leaveReviewForm.watch('rating') || 0;
    
    // Only apply feedback design if user has selected 1 or 2 stars
    if (currentRating >= 1 && currentRating <= 2 && feedbackDesign) {
      return feedbackDesign;
    }
    
    // Otherwise use review design (including when no stars are selected)
    return pageDesign;
  };

  // Function to get the star color (always from review form design)
  const getStarColor = () => {
    return pageDesign?.starsColor;
  };

  // Function to get the appropriate button color based on design type
  const getButtonColor = () => {
    const currentRating = leaveReviewForm.watch('rating') || 0;
    
    // Only apply feedback button color if user has selected 1 or 2 stars
    if (currentRating >= 1 && currentRating <= 2 && feedbackDesign?.feedbackButtonColor) {
      return feedbackDesign.feedbackButtonColor;
    }
    
    // Otherwise use the general buttons color from the current design
    return currentDesign?.buttonsColor;
  };

  const currentDesign = getCurrentDesign();

  // Show loading state while fetching design
  if (isLoadingDesign) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-baseBlue mx-auto mb-4"></div>
          <p className="text-base-coolGray">Loading design...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center flex-col min-h-screen py-4 sm:py-6 gap-4 sm:gap-6">
      <div className="bg-white text-center space-y-4 rounded w-full max-w-[845px] py-4 sm:py-6 px-4 sm:px-20">
        <div className="flex items-center gap-2">
          <div
            className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-lg shrink-0"
            style={{ color: `#424244`, background: `#42424414` }}
          >
            <span className="font-bold text-base sm:text-lg">
              {requestData?.data?.business?.name?.charAt(0)}
            </span>
          </div>
          <div className="flex flex-col">
            <h5 className="font-bold mr-auto break-all text-base sm:text-lg leading-none">
              {requestData?.data?.business?.name}
            </h5>
            <p className="text-base-coolGray text-xs sm:text-sm">
              {requestData?.data?.business?.address}
            </p>
          </div>
        </div>
      </div>
      <div className="bg-white space-y-4 rounded w-full max-w-[845px] py-6 sm:py-10 px-4 sm:px-20 pb-6">
        <div>
          <h2 
            className="font-bold text-2xl sm:text-3xl leading-none"
            style={currentDesign?.heading ? {} : {}}
          >
            {currentDesign?.heading || 'Leave a review'}
          </h2>
          <p className="text-base-coolGray mt-2 text-sm sm:text-base">
            {(currentDesign?.description || `How would you rate your experience with ${requestData?.data?.business?.name || 'this business'}?`).replace(/{{business-name}}/gi, requestData?.data?.business?.name || '')}
          </p>
        </div>
        <hr className="border-base-antiFlash" />
        <HookedForm
          formHook={leaveReviewForm}
          onSubmit={handleFormSubmit}
          className="flex gap-2 flex-col space-y-6"
        >
          <div>
            <h4 className="text-lg font-bold">
              Select stars to rate
            </h4>
            <ul className="flex gap-2 p-0 mt-4 justify-center sm:justify-start">
              {Array.from({ length: 5 }).map((_, index) => (
                <li key={index}>
                  <Star
                    active={ratingValue >= index + 1}
                    onClick={() => handleStarClick(index)}
                    color={getStarColor()}
                  />
                </li>
              ))}
            </ul>
          </div>
          {(leaveReviewForm?.watch('rating') || 0) >= 1 && (
            <>
              <hr className="border-base-antiFlash" />
              {/* Visibility field - always show for bad reviews */}
              {leaveReviewForm?.watch('rating') >= 1 && leaveReviewForm?.watch('rating') <= 2 && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="relative inline-block">
                    <GlobeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <select
                      className="w-auto pl-10 pr-8 py-3 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 appearance-none bg-gray-100"
                      {...leaveReviewForm.register('visibility')}
                    >
                      <option value="private">Non-public</option>
                      <option value="public">Public</option>
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <div className="relative group">
                    <div className="cursor-help">
                      <InfoIcon className="w-5 h-5" />
                    </div>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      The non-public form allows the company to respond to your concerns before a public rating is added.
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Bad reasons section - only show when visibility is private */}
              {leaveReviewForm?.watch('rating') >= 1 && leaveReviewForm?.watch('rating') <= 2 && (leaveReviewForm?.watch('visibility') === 'private') && (
                <div>
              <h4 className="text-lg font-bold">
                Select what made your experience bad
              </h4>
              <ul className="flex gap-2 p-0 mt-4 flex-wrap">
                {/* Use custom tags from feedback design if available, otherwise fall back to default */}
                {(currentDesign?.type === 'feedbackFormDesign' && currentDesign?.tags ? 
                  (typeof currentDesign.tags === 'string' ? currentDesign.tags.split(',') : currentDesign.tags).map((tag: string) => {
                    const isSelected = (leaveReviewForm
                      ?.watch('reasons') as string[] || [])
                      ?.includes(tag.trim());
                    return (
                      <button
                        key={tag.trim()}
                        type="button"
                        onClick={() => handleBadReason(tag.trim())}
                        className={clsx(
                          'rounded-full border py-1 px-3 text-[15px] cursor-pointer transition-all',
                          isSelected
                            ? 'bg-brand-lightBlue4 border-brand-baseBlue text-brand-baseBlue font-medium shadow-sm'
                            : 'border-base-ghostWhite bg-white text-base-davyGray hover:bg-gray-50'
                        )}
                      >
                        {tag.trim()}
                      </button>
                    );
                  }) :
                  BAD_REVIEW_REASONS.map((reason) => {
                    const isSelected = (leaveReviewForm
                      ?.watch('reasons') as string[] || [])
                    ?.includes(reason.value);
                  return (
                    <button
                      key={reason.value}
                      type="button"
                      onClick={() => handleBadReason(reason.value)}
                      className={clsx(
                        'rounded-full border py-1 px-3 text-[15px] cursor-pointer transition-all',
                        isSelected
                          ? 'bg-brand-lightBlue4 border-brand-baseBlue text-brand-baseBlue font-medium shadow-sm'
                          : 'border-base-ghostWhite bg-white text-base-davyGray hover:bg-gray-50'
                      )}
                    >
                        {reason.label}
                    </button>
                  );
                  })
                )}
              </ul>
                </div>
              )}
   
          <div>
            {/* Custom label with AI dropdown aligned on the same line */}
            <div className="flex items-center justify-between mb-4">
              <label htmlFor="review" className="text-lg font-bold">
                {currentDesign?.type === 'feedbackFormDesign' && currentDesign?.feedbackInput ? 
                  currentDesign.feedbackInput : 
                  ((leaveReviewForm?.watch('rating') || 0) >= 1 && (leaveReviewForm?.watch('rating') || 0) <= 2 && (leaveReviewForm?.watch('visibility') === 'private') ? 
                    'What can we do to improve your experience?' : 
                    'Review'
                  )
                }
              </label>
              {!previewOpen &&
                !isBasicPlan && (
                  <div className="relative group">
                    <div style={{ opacity: (leaveReviewForm.watch('review') && hasStoppedTyping) ? 1 : 0.5, pointerEvents: (leaveReviewForm.watch('review') && hasStoppedTyping) ? 'auto' : 'none' }}>
                      <AIDropdown
                        key="en"
                        actions={actionsFull.map((action) => ({
                          ...action,
                          label: action.label,
                        }))}
                      />
                    </div>
                    {(!leaveReviewForm.watch('review') || !hasStoppedTyping) && (
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
                        Describe your experience and our AI will help you improve your text
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                      </div>
                    )}
                  </div>
                )}
            </div>

            <TextField
              name="review"
              placeholder="Describe your case so we can help you solve it"
              onKeyUp={handleTypingPause}
            />
            {leaveReviewForm.watch('aiSuggestion') && (
              <div className="flex justify-end space-x-4 mt-2">
                <Button
                  kind="secondary"
                  type="button"
                  className="w-24 sm:w-28 text-xs py-1"
                  onClick={() => handleDeclineAiSuggestion()}
                >
                  No thanks
                </Button>
              </div>
            )}
            <p className="text-[10px] text-base-coolGray">
              By submitting this review, you agree to our{' '}
              <Link
                to="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-baseBlue"
              >
                Privacy Policy
              </Link>
            </p>
          </div>
          <label
            htmlFor="file"
            className="w-fit font-medium text-base-davyGray bg-base-ghostWhite p-2 rounded-lg cursor-pointer flex gap-2 items-center"
          >
            <AttachIcon />
            Attach Photos
          </label>

          <input
            id="file"
            name="images"
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.webp"
            multiple
            className="hidden"
            onChange={handleChange}
          />
          {thumbnails.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 relative z-0">
              {thumbnails.map((thumb, index) => (
                <div
                  key={index}
                  className="w-full aspect-square relative rounded-[4px] overflow-hidden"
                >
                  <img
                    src={thumb}
                    className="absolute w-full h-full object-cover object-center"
                    alt="Review photo"
                  />
                  <div className="bg-black/30 inset-0 absolute" />
                  <button
                    onClick={() => removeFile(index)}
                    type="button"
                    className="h-6 w-6 bg-white/50 flex items-center justify-center top-0 right-0 absolute z-10"
                    aria-label="Remove photo"
                  >
                    <svg
                      width="8"
                      height="8"
                      viewBox="0 0 8 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="shrink-0"
                    >
                      <path
                        d="M6.5 1.5L1.5 6.5M6.5 6.5L1.5 1.5"
                        stroke="black"
                        stroke-width="1.5"
                        stroke-linecap="round"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Reference contacts section - show for feedback form with feedbackReference enabled, or for bad reviews when no feedback form is provided, but hide when visibility is public */}
          {(
            (currentDesign?.type === 'feedbackFormDesign' && currentDesign?.feedbackReference) ||
            (!feedbackDesign && (leaveReviewForm?.watch('rating') || 0) >= 1 && (leaveReviewForm?.watch('rating') || 0) <= 2)
          ) && (leaveReviewForm?.watch('visibility') || 'private') === 'private' && (
            <div className="mt-6">
              <h4 className="text-lg font-bold mb-2">
                Reference contacts <span className="text-gray-400 text-sm font-normal">(Optional)</span>
              </h4>
              <p className="text-sm text-base-coolGray mb-4">
                Add your contact information so that the company knows which is the best channel to contact you
              </p>
              <div className="flex gap-4">
                <div className="flex-1">
                  <input
                    type="email"
                    placeholder="Email"
                    className="w-full p-3 border-0 bg-base-seasalt rounded-lg focus:outline-brand-baseBlue focus:outline-1"
                    {...leaveReviewForm.register('contactEmail')}
                  />
                </div>
                <div className="flex-1">
                  <PhoneField 
                    name="contactPhone" 
                    placeholder="Phone number"
                    isOptional={true}
                  />
                </div>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            {!showConfirmation ? (
              <>
                {/* Show platform buttons for public visibility with bad reviews, or when no page design exists for good reviews, otherwise show regular submit button */}
                {((leaveReviewForm?.watch('rating') || 0) >= 1 && (leaveReviewForm?.watch('rating') || 0) <= 2 && (leaveReviewForm?.watch('visibility') || 'private') === 'public') || 
                 (!feedbackDesign && (leaveReviewForm?.watch('rating') || 0) >= 1 && (leaveReviewForm?.watch('rating') || 0) <= 2 && (leaveReviewForm?.watch('visibility') || 'private') === 'public') ||
                 (!pageDesign && (leaveReviewForm?.watch('rating') || 0) >= 3) ? (
                  <div className="flex flex-col gap-2 w-full">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-base-coolGray text-xs font-normal">Your review text will be automatically copied to your clipboard, so you can simply click the button and paste it.</span>
                      <InfoIcon className="w-4 h-4 text-base-coolGray flex-shrink-0" />
                    </div>
                    {/* Show platform buttons based on design or default logic */}
                    {/* For page designs with specific platform */}
                    {pageDesign?.defaultPlatform === 'yelp' && yelpUrl && (
                      <button
                        type="button"
                        className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg bg-[#DC0000] hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                        style={{ 
                          minWidth: 220,
                          backgroundColor: getButtonColor() || '#DC0000'
                        }}
                        onClick={() => {
                          // Copy review text to clipboard
                          const reviewText = leaveReviewForm.watch('review') || '';
                          copyToClipboard(reviewText);
                          
                          // Store that Yelp was used
                          const reviewData = {
                            review: leaveReviewForm.watch('review') || '',
                            rating: leaveReviewForm.watch('rating') || 0,
                            yelpUrl: yelpUrl,
                            googleUrl: googleUrl,
                            businessName: requestData?.data?.business?.name,
                            businessAddress: requestData?.data?.business?.address,
                            thumbnails: thumbnails,
                            timestamp: new Date().toISOString(),
                            usedPlatform: 'yelp'
                          };
                          localStorage.setItem('reviewData', JSON.stringify(reviewData));
                          
                          // Submit review to database with isFeedback: true for public bad reviews
                          handleSubmit(leaveReviewForm.getValues(), true);
                          
                          // Open Yelp review page
                          window.open(yelpUrl, '_blank', 'noopener,noreferrer');
                          
                          // Redirect to thank you page
                          const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
                          navigate(thankYouUrl);
                        }}
                      >
                        <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                          <YelpIcon style={{ width: 20, height: 20 }} />
                        </div>
                        <span className="text-center">Leave this review on Yelp</span>
                      </button>
                    )}
                    {pageDesign?.defaultPlatform === 'google' && googleUrl && (
                      <button
                        type="button"
                        className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg bg-[#4285F4] hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                        style={{ 
                          minWidth: 220,
                          backgroundColor: getButtonColor() || '#4285F4'
                        }}
                        onClick={() => {
                          // Copy review text to clipboard
                          const reviewText = leaveReviewForm.watch('review') || '';
                          copyToClipboard(reviewText);
                          
                          // Store that Google was used
                          const reviewData = {
                            review: leaveReviewForm.watch('review') || '',
                            rating: leaveReviewForm.watch('rating') || 0,
                            yelpUrl: yelpUrl,
                            googleUrl: googleUrl,
                            businessName: requestData?.data?.business?.name,
                            businessAddress: requestData?.data?.business?.address,
                            thumbnails: thumbnails,
                            timestamp: new Date().toISOString(),
                            usedPlatform: 'google'
                          };
                          localStorage.setItem('reviewData', JSON.stringify(reviewData));
                          
                          // Submit review to database with isFeedback: true for public bad reviews
                          handleSubmit(leaveReviewForm.getValues(), true);
                          
                          // Open Google review page
                          window.open(googleUrl, '_blank', 'noopener,noreferrer');
                          
                          // Redirect to thank you page
                          const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
                          navigate(thankYouUrl);
                        }}
                      >
                        <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                          <GoogleIcon style={{ width: 20, height: 20 }} />
                        </div>
                        <span className="text-center">Leave this review on Google</span>
                      </button>
                    )}
                    {/* Fallback: show buttons based on page design or default logic */}
                    {((!pageDesign?.defaultPlatform && pageDesign) || (!pageDesign && !feedbackDesign)) && (
                      <>
                        {/* Default logic: Google first, then Yelp if Google not available */}
                        {googleUrl ? (
                          <button
                            type="button"
                            className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg bg-[#4285F4] hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                            style={{ 
                              minWidth: 220,
                              backgroundColor: getButtonColor() || '#4285F4'
                            }}
                            onClick={() => {
                              // Copy review text to clipboard
                              const reviewText = leaveReviewForm.watch('review') || '';
                              copyToClipboard(reviewText);
                              
                              // Store that Google was used
                              const reviewData = {
                                review: leaveReviewForm.watch('review') || '',
                                rating: leaveReviewForm.watch('rating') || 0,
                                yelpUrl: yelpUrl,
                                googleUrl: googleUrl,
                                businessName: requestData?.data?.business?.name,
                                businessAddress: requestData?.data?.business?.address,
                                thumbnails: thumbnails,
                                timestamp: new Date().toISOString(),
                                usedPlatform: 'google'
                              };
                              localStorage.setItem('reviewData', JSON.stringify(reviewData));
                              
                              // Submit review to database with isFeedback: true for public bad reviews
                              handleSubmit(leaveReviewForm.getValues(), true);
                              
                              // Open Google review page
                              window.open(googleUrl, '_blank', 'noopener,noreferrer');
                              
                              // Redirect to thank you page
                              const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
                              navigate(thankYouUrl);
                            }}
                          >
                            <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                              <GoogleIcon style={{ width: 20, height: 20 }} />
                            </div>
                            <span className="text-center">Leave this review on Google</span>
                          </button>
                        ) : yelpUrl ? (
                          <button
                            type="button"
                            className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg bg-[#DC0000] hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                            style={{ 
                              minWidth: 220,
                              backgroundColor: getButtonColor() || '#DC0000'
                            }}
                            onClick={() => {
                              // Copy review text to clipboard
                              const reviewText = leaveReviewForm.watch('review') || '';
                              copyToClipboard(reviewText);
                              
                              // Store that Yelp was used
                              const reviewData = {
                                review: leaveReviewForm.watch('review') || '',
                                rating: leaveReviewForm.watch('rating') || 0,
                                yelpUrl: yelpUrl,
                                googleUrl: googleUrl,
                                businessName: requestData?.data?.business?.name,
                                businessAddress: requestData?.data?.business?.address,
                                thumbnails: thumbnails,
                                timestamp: new Date().toISOString(),
                                usedPlatform: 'yelp'
                              };
                              localStorage.setItem('reviewData', JSON.stringify(reviewData));
                              
                              // Submit review to database with isFeedback: true for public bad reviews
                              handleSubmit(leaveReviewForm.getValues(), true);
                              
                              // Open Yelp review page
                              window.open(yelpUrl, '_blank', 'noopener,noreferrer');
                              
                              // Redirect to thank you page
                              const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
                              navigate(thankYouUrl);
                            }}
                          >
                            <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                              <YelpIcon style={{ width: 20, height: 20 }} />
                            </div>
                            <span className="text-center">Leave this review on Yelp</span>
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : 
                
                /* New section: Bad reviews + Public visibility + reviewFormDesign with additionalPlatforms */
                ((leaveReviewForm?.watch('rating') || 0) >= 1 && (leaveReviewForm?.watch('rating') || 0) <= 2 && 
                 (leaveReviewForm?.watch('visibility') || 'private') === 'public' && 
                 pageDesign?.type === 'reviewFormDesign' && pageDesign?.additionalPlatforms) ? (
                  <div className="flex flex-col gap-2 w-full">
                    {/* Show additional platform buttons */}
                    {pageDesign?.additionalPlatforms?.includes('google') && googleUrl && (
                      <button
                        type="button"
                        className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg bg-[#4285F4] hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                        style={{ 
                          minWidth: 220,
                          backgroundColor: getButtonColor() || '#4285F4'
                        }}
                        onClick={() => {
                          // Copy review text to clipboard
                          const reviewText = leaveReviewForm.watch('review') || '';
                          copyToClipboard(reviewText);
                          
                          // Store that Google was used
                          const reviewData = {
                            review: leaveReviewForm.watch('review') || '',
                            rating: leaveReviewForm.watch('rating') || 0,
                            yelpUrl: yelpUrl,
                            googleUrl: googleUrl,
                            businessName: requestData?.data?.business?.name,
                            businessAddress: requestData?.data?.business?.address,
                            thumbnails: thumbnails,
                            timestamp: new Date().toISOString(),
                            usedPlatform: 'google'
                          };
                          localStorage.setItem('reviewData', JSON.stringify(reviewData));
                          
                          // Submit review to database with isFeedback: true for bad reviews
                          handleSubmit(leaveReviewForm.getValues(), true);
                          
                          // Open Google review page
                          window.open(googleUrl, '_blank', 'noopener,noreferrer');
                          
                          // Redirect to thank you page
                          const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
                          navigate(thankYouUrl);
                        }}
                      >
                        <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                          <GoogleIcon style={{ width: 20, height: 20 }} />
                        </div>
                        <span className="text-center">Leave this review on Google</span>
                      </button>
                    )}
                    {pageDesign?.additionalPlatforms?.includes('yelp') && yelpUrl && (
                      <button
                        type="button"
                        className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg bg-[#DC0000] hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                        style={{ 
                          minWidth: 220,
                          backgroundColor: getButtonColor() || '#DC0000'
                        }}
                        onClick={() => {
                          // Copy review text to clipboard
                          const reviewText = leaveReviewForm.watch('review') || '';
                          copyToClipboard(reviewText);
                          
                          // Store that Yelp was used
                          const reviewData = {
                            review: leaveReviewForm.watch('review') || '',
                            rating: leaveReviewForm.watch('rating') || 0,
                            yelpUrl: yelpUrl,
                            googleUrl: googleUrl,
                            businessName: requestData?.data?.business?.name,
                            businessAddress: requestData?.data?.business?.address,
                            thumbnails: thumbnails,
                            timestamp: new Date().toISOString(),
                            usedPlatform: 'yelp'
                          };
                          localStorage.setItem('reviewData', JSON.stringify(reviewData));
                          
                          // Submit review to database with isFeedback: true for bad reviews
                          handleSubmit(leaveReviewForm.getValues(), true);
                          
                          // Open Yelp review page
                          window.open(yelpUrl, '_blank', 'noopener,noreferrer');
                          
                          // Redirect to thank you page
                          const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
                          navigate(thankYouUrl);
                        }}
                      >
                        <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                          <YelpIcon style={{ width: 20, height: 20 }} />
                        </div>
                        <span className="text-center">Leave this review on Yelp</span>
                      </button>
                    )}
                  </div>
                ) : (
                <Button
                  type="button"
                  onClick={handleSubmitClick}
                  disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2"
                    style={getButtonColor() ? { backgroundColor: getButtonColor() || '#4285F4' } : {}}
                    leftNode={
                      currentDesign?.type === 'reviewFormDesign' && currentDesign?.defaultPlatform ? (
                        currentDesign.defaultPlatform.toLowerCase() === 'google' ? (
                          <GoogleIcon style={{ width: 24, height: 24, fill: 'white' }} />
                        ) : currentDesign.defaultPlatform.toLowerCase() === 'yelp' ? (
                          <YelpIcon style={{ width: 24, height: 24, fill: 'white' }} />
                        ) : null
                      ) : null
                    }
                >
                  {isSubmitting
                        ? 'Submitting...'
                        : (currentDesign?.type === 'feedbackFormDesign' && currentDesign?.feedbackTextButton ? 
                            currentDesign.feedbackTextButton : 
                            (currentDesign?.defaultPlatform ? 
                              `Leave this review on ${currentDesign.defaultPlatform.charAt(0).toUpperCase() + currentDesign.defaultPlatform.slice(1)}` :
                              ((leaveReviewForm?.watch('rating') || 0) >= 1 && (leaveReviewForm?.watch('rating') || 0) <= 2 && (leaveReviewForm?.watch('visibility') || 'private') === 'private' ? 
                                'Send direct feedback' : 
                                'Submit Review'
                              )
                            )
                          )}
                </Button>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center gap-2 w-full">
                <p className="text-base-coolGray text-center">
                  Are you sure you want to submit this review?
                </p>
                <div className="flex flex-col gap-2 w-full">
                  {/* Show platform buttons for positive reviews (3+ stars) or bad reviews with public visibility, or when no page design exists */}
                  {(yelpUrl || googleUrl) && (((leaveReviewForm?.watch('rating') || 0) >= 3 || ((leaveReviewForm?.watch('rating') || 0) >= 1 && (leaveReviewForm?.watch('rating') || 0) <= 2) && (leaveReviewForm?.watch('visibility') || 'private') === 'public') || 
                   (!feedbackDesign && (leaveReviewForm?.watch('rating') || 0) >= 1 && (leaveReviewForm?.watch('rating') || 0) <= 2 && (leaveReviewForm?.watch('visibility') || 'private') === 'public') ||
                   (!pageDesign && (leaveReviewForm?.watch('rating') || 0) >= 3)) ? (
                    <div className="flex flex-col gap-2 w-full">
                      {/* Show only the default platform button if specified in design */}
                      {currentDesign?.defaultPlatform === 'yelp' && yelpUrl && (
                        <button
                          type="button"
                          className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                          style={{ 
                            minWidth: 220,
                            backgroundColor: getButtonColor() || '#DC0000'
                          }}
                          onClick={() => {
                            // Store that Yelp was used
                            const reviewData = {
                              review: leaveReviewForm.watch('review') || '',
                              rating: leaveReviewForm.watch('rating') || 0,
                              yelpUrl: yelpUrl,
                              googleUrl: googleUrl,
                              businessName: requestData?.data?.business?.name,
                              businessAddress: requestData?.data?.business?.address,
                              thumbnails: thumbnails,
                              timestamp: new Date().toISOString(),
                              usedPlatform: 'yelp'
                            };
                            localStorage.setItem('reviewData', JSON.stringify(reviewData));
                            
                            // Submit review to database with isFeedback: false for platform reviews
                            handleSubmit(leaveReviewForm.getValues(), true);
                            
                            // Open Yelp review page
                            window.open(yelpUrl, '_blank', 'noopener,noreferrer');
                            
                            // Redirect to thank you page for positive reviews with access token
                            const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
                            navigate(thankYouUrl);
                          }}
                        >
                          <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                            <YelpIcon style={{ width: 20, height: 20 }} />
                          </div>
                          <span className="text-center">Leave this review on Yelp</span>
                        </button>
                      )}
                      {currentDesign?.defaultPlatform === 'google' && googleUrl && (
                        <button
                          type="button"
                          className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg bg-[#4285F4] hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                          style={{ 
                            minWidth: 220,
                            backgroundColor: getButtonColor() || '#4285F4'
                          }}
                          onClick={() => {
                            // Store that Google was used
                            const reviewData = {
                              review: leaveReviewForm.watch('review') || '',
                              rating: leaveReviewForm.watch('rating') || 0,
                              yelpUrl: yelpUrl,
                              googleUrl: googleUrl,
                              businessName: requestData?.data?.business?.name,
                              businessAddress: requestData?.data?.business?.address,
                              thumbnails: thumbnails,
                              timestamp: new Date().toISOString(),
                              usedPlatform: 'google'
                            };
                            localStorage.setItem('reviewData', JSON.stringify(reviewData));
                            
                            // Submit review to database with isFeedback: false for platform reviews
                            handleSubmit(leaveReviewForm.getValues(), true);
                            
                            // Open Google review page
                            window.open(googleUrl, '_blank', 'noopener,noreferrer');
                            
                            // Redirect to thank you page for positive reviews with access token
                            const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
                            navigate(thankYouUrl);
                          }}
                        >
                          <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                            <GoogleIcon style={{ width: 20, height: 20 }} />
                          </div>
                          <span className="text-center">Leave this review on Google</span>
                        </button>
                      )}
                      {/* Fallback: show buttons based on page design or default logic */}
                      {((!currentDesign?.defaultPlatform && currentDesign) || (!pageDesign && !feedbackDesign)) && (
                        <>
                        {/* Default logic: Google first, then Yelp if Google not available */}
                        {googleUrl ? (
                          <button
                            type="button"
                            className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg bg-[#4285F4] hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                            style={{ 
                              minWidth: 220,
                              backgroundColor: getButtonColor() || '#4285F4'
                            }}
                            onClick={() => {
                              // Copy review text to clipboard
                              const reviewText = leaveReviewForm.watch('review') || '';
                              copyToClipboard(reviewText);
                              
                              // Store that Google was used
                              const reviewData = {
                                review: leaveReviewForm.watch('review') || '',
                                rating: leaveReviewForm.watch('rating') || 0,
                                yelpUrl: yelpUrl,
                                googleUrl: googleUrl,
                                businessName: requestData?.data?.business?.name,
                                businessAddress: requestData?.data?.business?.address,
                                thumbnails: thumbnails,
                                timestamp: new Date().toISOString(),
                                usedPlatform: 'google'
                              };
                              localStorage.setItem('reviewData', JSON.stringify(reviewData));
                              
                              // Submit review to database with isFeedback: false for platform reviews
                              handleSubmit(leaveReviewForm.getValues(), true);
                              
                              // Open Google review page
                              window.open(googleUrl, '_blank', 'noopener,noreferrer');
                              
                              // Redirect to thank you page for positive reviews
                              navigate(`/leave-review/${requestId}/thank-you?t=${accessToken}`);
                            }}
                          >
                            <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                              <GoogleIcon style={{ width: 20, height: 20 }} />
                            </div>
                            <span className="text-center">Leave this review on Google</span>
                          </button>
                        ) : yelpUrl ? (
                          <button
                            type="button"
                            className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg bg-[#DC0000] hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                            style={{ 
                              minWidth: 220,
                              backgroundColor: getButtonColor() || '#DC0000'
                            }}
                            onClick={() => {
                              // Copy review text to clipboard
                              const reviewText = leaveReviewForm.watch('review') || '';
                              copyToClipboard(reviewText);
                              
                              // Store that Yelp was used
                              const reviewData = {
                                review: leaveReviewForm.watch('review') || '',
                                rating: leaveReviewForm.watch('rating') || 0,
                                yelpUrl: yelpUrl,
                                googleUrl: googleUrl,
                                businessName: requestData?.data?.business?.name,
                                businessAddress: requestData?.data?.business?.address,
                                thumbnails: thumbnails,
                                timestamp: new Date().toISOString(),
                                usedPlatform: 'yelp'
                              };
                              localStorage.setItem('reviewData', JSON.stringify(reviewData));
                              
                              // Submit review to database with isFeedback: false for platform reviews
                              handleSubmit(leaveReviewForm.getValues(), true);
                              
                              // Open Yelp review page
                              window.open(yelpUrl, '_blank', 'noopener,noreferrer');
                              
                              // Redirect to thank you page for positive reviews
                              navigate(`/leave-review/${requestId}/thank-you?t=${accessToken}`);
                            }}
                          >
                            <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                              <YelpIcon style={{ width: 20, height: 20 }} />
                            </div>
                            <span className="text-center">Leave this review on Yelp</span>
                          </button>
                        ) : null}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 w-full">
                      {/* For bad reviews (1-2 stars) or no rating, show submit button */}
                      <Button
                        kind="secondary"
                        type="button"
                        onClick={handleCancelSubmit}
                        disabled={isSubmitting}
                        className="w-full"
                        style={{ 
                          minWidth: 220,
                          borderColor: getButtonColor() || undefined,
                          color: getButtonColor() || undefined
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                    const rating = leaveReviewForm.watch('rating') || 0;
                    const visibility = leaveReviewForm.watch('visibility') || 'private';
                    
                    // For bad reviews (1-2 stars) with private visibility, submit as feedback
                    if (rating >= 1 && rating <= 2 && visibility === 'private') {
                      handleSubmit(leaveReviewForm.getValues(), true);
                      return;
                    }
                    
                    // If in reviewFormDesign mode with defaultPlatform, handle platform submission directly
                    if (currentDesign?.type === 'reviewFormDesign' && currentDesign?.defaultPlatform) {
                      const platform = currentDesign.defaultPlatform.toLowerCase();
                      const platformUrl = platform === 'google' ? googleUrl : platform === 'yelp' ? yelpUrl : null;
                      
                      if (platformUrl) {
                        // Copy review text to clipboard
                        const reviewText = leaveReviewForm.watch('review') || '';
                        copyToClipboard(reviewText);
                        
                        // Store that this platform was used
                        const reviewData = {
                          review: leaveReviewForm.watch('review') || '',
                          rating: rating,
                          yelpUrl: yelpUrl,
                          googleUrl: googleUrl,
                          businessName: requestData?.data?.business?.name,
                          businessAddress: requestData?.data?.business?.address,
                          thumbnails: thumbnails,
                          timestamp: new Date().toISOString(),
                          usedPlatform: platform
                        };
                        localStorage.setItem('reviewData', JSON.stringify(reviewData));
                        
                        // Submit review to database with isFeedback: false for platform reviews
                        handleSubmit(leaveReviewForm.getValues(), false);
                        
                        // Open platform review page
                        window.open(platformUrl, '_blank', 'noopener,noreferrer');
                        
                        // Redirect to thank you page for positive reviews
                        const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
                        navigate(thankYouUrl);
                      } else {
                        // Fallback to regular submission
                        handleSubmit(leaveReviewForm.getValues(), false);
                      }
                    } else {
                      // Regular submission for other cases
                      handleSubmit(leaveReviewForm.getValues(), false);
                    }
                  }}
                        disabled={isSubmitting}
                        className="w-full"
                                          style={{ 
                    minWidth: 220,
                    backgroundColor: (currentDesign?.type === 'reviewFormDesign' && currentDesign?.buttonsColor) ? 
                      currentDesign.buttonsColor : 
                      (getButtonColor() || undefined)
                  }}
                      >
                        {isSubmitting
                          ? 'Submitting...'
                          : (currentDesign?.type === 'feedbackFormDesign' && currentDesign?.feedbackTextButton ? 
                              currentDesign.feedbackTextButton : 
                              ((leaveReviewForm?.watch('rating') || 0) >= 1 && (leaveReviewForm?.watch('rating') || 0) <= 2 && (leaveReviewForm?.watch('visibility') || 'private') === 'private' ? 
                                'Send direct feedback' : 
                                'Yes, Submit'
                              )
                            )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
            </>
          )}
        </HookedForm>
      </div>
      <p className="mt-auto text-base-coolGray">
        Powered by Echo
        <Link to="/sign-up" className="ml-2 text-brand-baseBlue font-bold">
          Echo Ratings
        </Link>
      </p>
      <button
        onClick={() => window.open('/faq', '_blank')}
        className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 bg-brand-baseBlue text-white p-2 sm:p-3 rounded-full shadow-lg flex items-center gap-2 hover:bg-brand-baseBlue/90 transition-colors duration-200"
        aria-label="Customer Support"
      >
        <HelpDeskIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        <span className="hidden sm:inline">
          Customer Support
        </span>
      </button>
      <BaseDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Review Preview"
      >
        <PreviewReviewBox
          review={previewReview}
          onReviewUpdate={(updatedReview) => {
            leaveReviewForm.setValue('review', updatedReview);
            setPreviewOpen(false);
          }}
          onClose={() => setPreviewOpen(false)}
          thumbnails={thumbnails}
          onThumbnailsUpdate={setThumbnails}
          accessToken={accessToken}
          isBasicPlan={isBasicPlan}
        />
      </BaseDialog>
    </div>
  );
};
